import ClearIcon from '@mui/icons-material/Clear';
import { Box } from '@mui/material';
import classNames from 'classnames';
import * as React from 'react';
import { memo, useCallback } from 'react';

import { FlightTurnaroundDto, Resource, ResourceType, useQueryGetGetResources } from '../../../../../../../backend';
import { getTurnaroundActualOccupation } from '../../../../RmsContent.utils';
import { useRmsGanttState, useRmsGanttValue } from '../../../../RmsContext';
import { ganttOneMinuteInMilliSeconds, ganttPxPerMs } from '../../../gantt.constants';
import { getResourceName } from '../../../gantt.utils';
import { TurnaroundData } from './TurnaroundData';
import {
    StyledColorBox,
    StyledIconButton,
    StyledIndicatorsBox,
    StyledTurnaroundFlightNumberBox,
    StyledTurnaroundZoneBox,
} from './TurnaroundZoneBox.style';

const getColorByTerminal = (terminal: string, allTerminals: Resource[]) => {
    if (!terminal) return '';
    if (allTerminals?.length > 0 && terminal === allTerminals[0]?.name) {
        return '#34A0A0';
    } else if (allTerminals?.length > 0 && terminal === allTerminals[1]?.name) {
        return '#7986CB';
    } else {
        return '';
    }
};
export const TurnaroundZoneBox = memo(function TurnaroundZoneBox({
    turnaround,
    resourceType,
    isProposition,
}: {
    turnaround: FlightTurnaroundDto;
    resourceType: ResourceType;
    isProposition: boolean;
}) {
    const selectedTurnaround = useRmsGanttValue('selectedTurnarounds')?.[0];
    const isFocused = useRmsGanttValue('isFocused');
    const { data: allResources } = useQueryGetGetResources({ includeNonActive: true });
    const allTerminals = allResources?.filter(r => r.type === ResourceType.TERMINAL);
    const turnaroundOccupation = getTurnaroundActualOccupation(turnaround);
    const [proposedAllocation, setProposedAllocation] = useRmsGanttState('proposedAllocation');
    const [nbUpdateAllocations, setNbUpdateAllocations] = useRmsGanttState('nbUpdateAllocations');

    const occupation = turnaroundOccupation?.occupationEnd?.time - turnaroundOccupation?.occupationStart?.time || null;

    const handleCancel = useCallback(() => {
        const newProposition = proposedAllocation?.filter(
            o =>
                o.turnaround.arr !== turnaround?.arrivalFlight?.id &&
                o.turnaround.dep !== turnaround?.departureFlight?.id,
        );
        setProposedAllocation([
            ...newProposition,
            {
                turnaround: { arr: turnaround?.arrivalFlight?.id, dep: turnaround?.departureFlight?.id },
                resource: getResourceName(turnaround),
            },
        ]);
        if (!!turnaround?.arrivalFlight && !!turnaround?.departureFlight) {
            setNbUpdateAllocations(nbUpdateAllocations - 2);
        } else {
            setNbUpdateAllocations(nbUpdateAllocations - 1);
        }
    }, [proposedAllocation, setProposedAllocation, turnaround, setNbUpdateAllocations, nbUpdateAllocations]);

    const tebt =
        nbUpdateAllocations === 0 &&
        resourceType === ResourceType.BOARDING_GATE &&
        turnaround?.considerDepartureFlight &&
        turnaround?.departureFlight?.tebt &&
        turnaroundOccupation?.occupationStart &&
        turnaround.departureFlight.tebt < turnaroundOccupation.occupationEnd.time
            ? turnaround.departureFlight.tebt - turnaroundOccupation.occupationStart.time
            : null;

    return (
        <StyledTurnaroundZoneBox
            className={classNames({
                cancelled: turnaround?.arrivalFlight?.cancelled || turnaround?.departureFlight?.cancelled,
                alert:
                    !isProposition &&
                    (turnaround?.arrivalFlight?.alertsOnAllocatedResources?.includes(resourceType) ||
                        turnaround?.departureFlight?.alertsOnAllocatedResources?.includes(resourceType)),
            })}
        >
            {tebt > 0 && (
                <Box
                    height={'18px'}
                    position={'absolute'}
                    marginLeft={`${Math.trunc(tebt * ganttPxPerMs * 2 - 1)}px`}
                    borderTop={'5px solid #0096FF'}
                    borderBottom={'5px solid #0096FF'}
                    zIndex={4}
                    width={'5px'}
                />
            )}
            <StyledIndicatorsBox ml={'2px'}>
                {turnaround?.considerArrivalFlight && (
                    <StyledColorBox
                        terminalcolor={getColorByTerminal(turnaround?.arrivalFlight?.arrivalTerminal, allTerminals)}
                    />
                )}
            </StyledIndicatorsBox>
            {isFocused &&
            isProposition &&
            selectedTurnaround?.arrivalFlight?.id === turnaround?.arrivalFlight?.id &&
            selectedTurnaround?.departureFlight?.id === turnaround?.departureFlight?.id ? (
                <StyledIconButton size={'small'} onClick={handleCancel}>
                    <ClearIcon />
                </StyledIconButton>
            ) : (
                (!turnaround?.arrivalFlight ||
                    !turnaround?.departureFlight ||
                    (occupation && occupation >= 25 * ganttOneMinuteInMilliSeconds)) && (
                    <StyledTurnaroundFlightNumberBox>
                        <TurnaroundData
                            turnaround={turnaround}
                            occupation={occupation}
                            addTargetBoardingTimesIcon={resourceType === ResourceType.BOARDING_GATE}
                        />
                    </StyledTurnaroundFlightNumberBox>
                )
            )}
            <StyledIndicatorsBox mr={'2px'}>
                {turnaround?.considerDepartureFlight && turnaround?.departureFlight?.departureTerminal && (
                    <StyledColorBox
                        terminalcolor={getColorByTerminal(turnaround.departureFlight.departureTerminal, allTerminals)}
                    />
                )}
            </StyledIndicatorsBox>
        </StyledTurnaroundZoneBox>
    );
});
